/**
 * Öffentlicher Verkehr: Haltestelle finden, Abfahrten holen.
 *
 * Quelle ist transport.opendata.ch, dieselbe wie bei der Verbindungssuche.
 * Ohne Schlüssel, mit freigegebenem CORS, rund 0,4 Sekunden je Abfrage - und
 * mit dem, worauf es an einer Haltestelle ankommt: Linie, Ziel, Zeit,
 * Verspätung, Gleis.
 *
 * Der Dienst wird ehrenamtlich betrieben und ist auf 1000 Abfragen pro Tag
 * begrenzt. Deshalb wird nur gefragt, wenn jemand eine Haltestelle antippt -
 * nicht im Hintergrund, nicht auf Vorrat.
 */
const BASIS = 'https://transport.opendata.ch/v1';

/** POI-Klassen, die eine Abfahrtstafel verdienen. */
export const HALT_KLASSEN = new Set([
  'bus', 'railway', 'rail', 'tram', 'subway', 'ferry_terminal', 'aerialway'
]);

const cache = new Map();

async function hole(pfad, signal) {
  const res = await fetch(`${BASIS}/${pfad}`, { signal });
  if (!res.ok) throw new Error(`Fahrplan ${res.status}`);
  return res.json();
}

/**
 * Nächstgelegene Haltestelle zu einem Punkt. Die Suche über Koordinaten ist
 * verlässlicher als über den Namen: "Bahnhof" gibt es hundertfach, und OSM
 * schreibt Haltestellen anders als der Fahrplan.
 */
export async function haltestelleBei({ lat, lng }, { signal } = {}) {
  const key = `h:${lat.toFixed(4)},${lng.toFixed(4)}`;
  if (cache.has(key)) return cache.get(key);
  const p = (async () => {
    const d = await hole(`locations?x=${lng}&y=${lat}`, signal);
    // Ohne `id` ist es eine Adresse, keine Haltestelle.
    const treffer = (d.stations || []).filter((s) => s.id);
    if (!treffer.length) return null;
    const s = treffer[0];
    return { id: s.id, name: s.name, distanz: s.distance ?? null };
  })().catch(() => null);
  cache.set(key, p);
  return p;
}

/** Nächste Abfahrten. Bewusst nicht zwischengespeichert - sie ändern sich. */
export async function abfahrten(stationId, { limit = 8, signal } = {}) {
  const d = await hole(`stationboard?id=${encodeURIComponent(stationId)}&limit=${limit}`, signal);
  return (d.stationboard || []).map((j) => {
    const s = j.stop || {};
    const zeit = s.departureTimestamp ? new Date(s.departureTimestamp * 1000) : null;
    return {
      linie: `${j.category || ''}${j.number ? ' ' + j.number : ''}`.trim() || j.name || '',
      kuerzel: `${j.category || ''}${j.number || ''}`.trim(),
      // Getrennt, weil das Linienzeichen beides einzeln braucht.
      kategorie: j.category || '',
      nummer: j.number ? String(j.number) : '',
      ziel: j.to || '',
      zeit,
      verspaetung: Number.isFinite(s.delay) ? s.delay : null,
      gleis: s.platform || '',
      // Der Fahrplan liefert die weiteren Halte gleich mit - das spart eine
      // zweite Abfrage, wenn jemand die Fahrt öffnet.
      halte: (j.passList || [])
        .map((h) => {
          const k = h.station?.coordinate || {};
          return {
            name: h.station?.name || '',
            // Achtung: der Dienst nennt die Breite `x` und die Länge `y`.
            lat: Number.isFinite(k.x) ? k.x : null,
            lng: Number.isFinite(k.y) ? k.y : null,
            zeit: h.departureTimestamp ? new Date(h.departureTimestamp * 1000)
              : (h.arrivalTimestamp ? new Date(h.arrivalTimestamp * 1000) : null),
            verspaetung: Number.isFinite(h.delay) ? h.delay : null
          };
        })
        .filter((h) => h.name)
    };
  });
}

/** Minuten bis zur Abfahrt - "jetzt", "4 min", sonst die Uhrzeit. */
export function inKuerze(zeit) {
  if (!zeit) return '';
  const min = Math.round((zeit.getTime() - Date.now()) / 60000);
  // Was gerade abfaehrt, steht als "jetzt" - "abgefahren" waere zwar genauer,
  // aber an der Haltestelle nutzlos. Aelteres filtert die Tafel ohnehin weg.
  if (min <= 0) return 'jetzt';
  if (min <= 30) return `${min} min`;
  return `${String(zeit.getHours()).padStart(2, '0')}:${String(zeit.getMinutes()).padStart(2, '0')}`;
}

/* ------------------------------------------------- Linienverlauf aus OSM */

const OVERPASS = [
  'https://overpass.osm.ch/api/interpreter',
  'https://overpass-api.de/api/interpreter'
];

async function overpass(query, signal) {
  let fehler = null;
  for (const url of OVERPASS) {
    try {
      const r = await fetch(url, { method: 'POST', signal, body: query });
      if (!r.ok) throw new Error(`Overpass ${r.status}`);
      return (await r.json()).elements || [];
    } catch (e) {
      if (e.name === 'AbortError') throw e;
      fehler = e;
    }
  }
  throw fehler;
}

/**
 * Teilstücke zu einer durchgehenden Linie zusammensetzen.
 *
 * Die Wege einer Relation stehen zwar in der Reihenfolge der Fahrt, aber nicht
 * zwingend in der richtigen Richtung - OSM-Wege sind so digitalisiert, wie es
 * gerade passte. Deshalb wird jedes Stück so angehängt, dass sein Anfang am
 * bisherigen Ende liegt.
 */
function zusammensetzen(wege) {
  const linie = [];
  for (const w of wege) {
    const pts = w.geometry.map((g) => [g.lon, g.lat]);
    if (!pts.length) continue;
    if (!linie.length) { linie.push(...pts); continue; }
    const ende = linie[linie.length - 1];
    const nah = (a, b) => Math.abs(a[0] - b[0]) < 1e-6 && Math.abs(a[1] - b[1]) < 1e-6;
    if (nah(ende, pts[0])) linie.push(...pts.slice(1));
    else if (nah(ende, pts[pts.length - 1])) linie.push(...pts.reverse().slice(1));
    else linie.push(...pts);          // Lücke: lieber ein Sprung als gar nichts
  }
  return linie;
}

/**
 * Tatsächlicher Verlauf einer Linie. OSM führt Buslinien, Trams und Züge als
 * Relationen mit demselben Kürzel, das auch im Fahrplan steht - damit lässt
 * sich der echte Verlauf zeichnen statt einer Luftlinie zwischen den Halten.
 *
 * Gemessen für IR70 Zürich-Luzern: 212 KB in 0,5 Sekunden, 1686 Punkte.
 */
export async function linienVerlauf({ ref, ziel, lat, lng }, { signal } = {}) {
  if (!ref) return null;
  const sauber = ref.replace(/"/g, '');
  const q = `[out:json][timeout:30];
relation(around:250,${lat.toFixed(5)},${lng.toFixed(5)})[type=route][ref="${sauber}"];
out geom;`;
  let rel;
  try {
    rel = await overpass(q, signal);
  } catch { return null; }
  if (!rel.length) return null;

  // Richtung wählen: die Relation, deren Ziel zum Fahrplan passt.
  const zielKlein = String(ziel || '').toLowerCase();
  const passend = rel.find((r) => {
    const t = r.tags || {};
    const text = `${t.to || ''} ${t.name || ''}`.toLowerCase();
    return zielKlein && text.includes(zielKlein.split(' ')[0]);
  }) || rel[0];

  const wege = (passend.members || []).filter((m) => m.type === 'way' && m.geometry?.length);
  const linie = zusammensetzen(wege);
  return linie.length > 1 ? { coordinates: linie, name: passend.tags?.name || sauber } : null;
}
